import React, { Fragment, useState, useEffect, useMemo, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Table,
  Alert,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import FilePreview from "../Common/FilePreview";
import swal from "sweetalert";
import Select from "react-select";

const BillingEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const workOrderFromUrl = searchParams.get("workOrder") || "";
  const projectFromUrl = searchParams.get("project") || "";
  const BOQ_PAGE_SIZE = 10;
  const [boqIndex, setBoqIndex] = useState(0);
  const isInitialLoadRef = useRef(true);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [boqItems, setBoqItems] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [paymentGroups, setPaymentGroups] = useState([]);
  const [groupStages, setGroupStages] = useState({});
  const [billingMode, setBillingMode] = useState("BOQ"); // BOQ | REPORT
  const [reportItemsCache, setReportItemsCache] = useState([]);
  const [paymentMode, setPaymentMode] = useState("STANDARD");

  const [workOrders, setWorkOrders] = useState([]);
  const [showStageModal, setShowStageModal] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(null);

  const [directStages, setDirectStages] = useState({
    installation: false,
    tc: false,
    handover: false,
  });

  const [reports, setReports] = useState({
    MIR: [],
    JMR: [],
    TC: [],
    Handover: [],
  });
  const [offices, setOffices] = useState([]);
  const [currentWorkOrder, setCurrentWorkOrder] = useState(null);
  const [form, setForm] = useState({
    invoiceNumber: "",
    workOrder: "",
    stages: [],
    linkedReports: [],
    lineItems: [],

    subTotal: 0,

    cgst: 0,
    sgst: 0,
    igst: 0,

    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,

    totalAmount: 0,

    status: "",
    invoiceDocument: null,
    remarks: "",
    paidAmount: 0,
  });

  const filteredWorkOrders = useMemo(() => {
    let activeProjectId = projectFromUrl;
    if (!activeProjectId && form.workOrder && workOrders.length > 0) {
      const found = workOrders.find((w) => String(w._id) === String(form.workOrder));
      activeProjectId = found?.project?._id || found?.project;
    }
    if (!activeProjectId && workOrderFromUrl && workOrders.length > 0) {
      const found = workOrders.find((w) => String(w._id) === String(workOrderFromUrl));
      activeProjectId = found?.project?._id || found?.project;
    }
    if (!activeProjectId) return workOrders;
    return workOrders.filter((wo) => {
      const pId = wo.project?._id || wo.project;
      return String(pId) === String(activeProjectId);
    });
  }, [workOrders, projectFromUrl, form.workOrder, workOrderFromUrl]);

  const reportTypes = ["MIR", "JMR", "TC", "Handover"];
  const stageToReportMap = {
    Supply: ["MIR"],
    Installation: ["JMR"],
    "Testing & Commissioning": ["TC"],
    Handover: ["Handover"],
  };

  const normalizeTerms = (terms) => (Array.isArray(terms) ? terms : []);

  // Resolve payment config: item-level → group → WO global
  const getPaymentConfigForBoq = (boq) => {
    const itemTerms = normalizeTerms(boq?.paymentTerms);
    if (itemTerms.length) {
      return {
        mode: boq.paymentMode || paymentMode || "STANDARD",
        terms: itemTerms,
        groupKey: null,
      };
    }

    // Group-level fallback
    if (boq?._id && paymentGroups.length) {
      const boqKey = String(boq._id);
      const gIdx = paymentGroups.findIndex((g) =>
        (g.boqItemIds || []).map(String).includes(boqKey)
      );
      if (gIdx >= 0 && normalizeTerms(paymentGroups[gIdx].paymentTerms).length) {
        return {
          mode: paymentGroups[gIdx].paymentMode || paymentMode || "STANDARD",
          terms: normalizeTerms(paymentGroups[gIdx].paymentTerms),
          groupKey: `group_${gIdx}`,
        };
      }
    }

    return {
      mode: paymentMode || "STANDARD",
      terms: paymentTerms,
      groupKey: "default",
    };
  };

  // Get effective selected stages for a BOQ item (group-aware)
  const getEffectiveStagesForBoq = (boq) => {
    if (!paymentGroups.length) return form.stages;
    const config = getPaymentConfigForBoq(boq);
    const key = config.groupKey || "default";
    return groupStages[key] || [];
  };

  const getTermForStage = (stage, boq = null) => {
    const config = getPaymentConfigForBoq(boq);
    const target = String(stage || "").trim().toLowerCase();
    return (config.terms || []).find((t) =>
      (t.stage && String(t.stage).trim().toLowerCase() === target) ||
      (t.name && String(t.name).trim().toLowerCase() === target)
    );
  };

  const getTotalPercentForStages = (selectedStages, boq = null) => {
    const effectiveStages =
      selectedStages !== undefined && selectedStages !== null
        ? selectedStages
        : boq && paymentGroups.length
        ? getEffectiveStagesForBoq(boq)
        : form.stages || [];
    return effectiveStages.reduce(
      (sum, stage) => sum + Number(getTermForStage(stage, boq)?.percentage || 0),
      0,
    );
  };

  const allSelectedStages = paymentGroups.length
    ? [...new Set(Object.values(groupStages).flat())]
    : form.stages;

  const stages =
    paymentMode === "STANDARD"
      ? ["Supply", "Installation", "Testing & Commissioning", "Handover"]
      : paymentTerms.map((t) => t.name);

  const getStagesForGroup = (gIdx) => {
    const g = paymentGroups[gIdx];
    if (!g) return stages;
    return g.paymentMode === "STANDARD"
      ? ["Supply", "Installation", "Testing & Commissioning", "Handover"]
      : normalizeTerms(g.paymentTerms).map((t) => t.name);
  };

  const getStagesForDefault = () => stages;

  const standardReportTypes = allSelectedStages.flatMap(
    (stage) => stageToReportMap[stage] || [],
  );
  const visibleReportTypes = standardReportTypes.length > 0
    ? [...new Set(standardReportTypes)]
    : ["MIR", "JMR", "TC", "Handover"];

  const groupItemsByReport = (items) => {
    const groups = {};

    items.forEach((item, index) => {
      const type = item.source?.reportType || "BOQ";
      const id = item.source?.reportId || "manual";
      const number = item.source?.reportNumber || null;

      const key = `${type}-${id}`;

      if (!groups[key]) {
        groups[key] = {
          reportType: type,
          reportId: id,
          reportNumber: number,
          items: [],
        };
      }

      groups[key].items.push({
        ...item,
        originalIndex: index,
      });
    });

    return Object.values(groups);
  };

  const reportToStage = {
    MIR: "Supply",
    JMR: "Installation",
    TC: "Testing & Commissioning",
    Handover: "Handover",
    MEASUREMENT_SHEET: "Installation",
    BOQ: "BOQ",
  };
  const reportOrder = ["MIR", "JMR", "TC", "Handover", "BOQ"];

  const groupedItems = groupItemsByReport(form.lineItems).sort(
    (a, b) =>
      reportOrder.indexOf(a.reportType) - reportOrder.indexOf(b.reportType),
  );

  const units = [
    "mm",
    "cm",
    "pcs",
    "kg",
    "litre",
    "meter",
    "box",
    "sqft",
    "cft",
    "ton",
  ];
  const statuses = [
    "Draft",
    "Submitted",
    "Paid",
    "Partially Paid",
  ];

  // ----------------------------
  // INIT FETCHES
  // ----------------------------
  useEffect(() => {
    fetchWorkOrders();
    fetchOffices();
    if (id) {
      fetchBilling();
    }
  }, [id]);

  useEffect(() => {
    if (!currentWorkOrder) return;
    const clientState = getClientState(currentWorkOrder);
    const officeState = getOfficeState(currentWorkOrder);

    if (clientState && officeState) {
      const { cgst, sgst, igst } = applyGST(clientState, officeState);
      setForm((prev) => {
        if (prev.cgst === cgst && prev.sgst === sgst && prev.igst === igst) {
          return prev;
        }
        return { ...prev, cgst, sgst, igst };
      });
    }
  }, [currentWorkOrder, offices]);

  const recalculateBoqLineItems = (lineItems, selectedStages, grpStages = groupStages) => {
    if (!boqItems.length) return lineItems;
    return (lineItems || []).map((item) => {
      if (item.boqItemId) {
        const boq = boqItems.find((b) => String(b._id) === String(item.boqItemId));
        if (boq) {
          let stagesForBoq = selectedStages;
          if (paymentGroups.length) {
            const config = getPaymentConfigForBoq(boq);
            const key = config.groupKey || "default";
            stagesForBoq = grpStages[key] || [];
          }
          const itemPercent = getTotalPercentForStages(stagesForBoq, boq);
          const rate = (Number(boq.price || 0) * itemPercent) / 100;
          return {
            ...item,
            rate: rate.toFixed(2),
            amount: (Number(item.quantity || 0) * rate).toFixed(2),
            stage: stagesForBoq && stagesForBoq.length > 0 ? stagesForBoq.join(", ") : "",
          };
        }
      }
      return item;
    });
  };

  const loadNextBoqItems = () => {
    if (!form.stages?.length) return;
    const hasPaymentTermsForSelection = boqItems.some(
      (boq) => getTotalPercentForStages(form.stages, boq) > 0,
    );

    if (!hasPaymentTermsForSelection) return;

    const nextItems = boqItems.slice(boqIndex, boqIndex + BOQ_PAGE_SIZE);

    const mapped = nextItems.map((b) => {
      const itemPercent = getTotalPercentForStages(form.stages, b);
      const rate = (Number(b.price) * itemPercent) / 100;

      return {
        wlino: b.wlino || "",
        hsnSac: b.hsnSac || "",

        description: b.itemName,
        quantity: b.quantity,
        unit: b.unit,
        rate: rate.toFixed(2),
        amount: (b.quantity * rate).toFixed(2),
        boqItemId: b._id,

        // IMPORTANT — same as AddBilling
        stage: form.stages[0] || undefined,
        directStage: null,
      };
    });

    setForm((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, ...mapped],
    }));

    setBoqIndex((prev) => prev + BOQ_PAGE_SIZE);
  };

  useEffect(() => {
    if (billingMode !== "BOQ") return;
    if (!boqItems.length) return;

    // If editing and items exist → keep them
    if (form.lineItems && form.lineItems.length > 0) return;

    // Group mode — load ALL
    if (paymentGroups.length) {
      const anyStageSelected = Object.values(groupStages).some((s) => s.length > 0);
      if (!anyStageSelected) return;

      const allItems = boqItems.map((b) => {
        const itemPercent = getTotalPercentForStages([], b);
        const rate = (Number(b.price) * itemPercent) / 100;
        return {
          wlino: b.wlino || "",
          hsnSac: b.hsnSac || "",
          description: b.itemName,
          quantity: b.quantity,
          unit: b.unit,
          rate: rate.toFixed(2),
          amount: (b.quantity * rate).toFixed(2),
          boqItemId: b._id,
          directStage: null,
        };
      });

      setBoqIndex(0);
      setForm((prev) => ({ ...prev, lineItems: allItems }));
      setBoqIndex(boqItems.length);
      return;
    }

    // No groups — load ALL
    if (!form.stages.length || !paymentTerms.length) return;

    const hasPaymentTermsForSelection = boqItems.some(
      (boq) => getTotalPercentForStages(form.stages, boq) > 0,
    );
    if (!hasPaymentTermsForSelection) return;

    setBoqIndex(0);

    const allItems = boqItems.map((b) => {
      const itemPercent = getTotalPercentForStages(form.stages, b);
      const rate = (Number(b.price) * itemPercent) / 100;
      return {
        wlino: b.wlino || "",
        hsnSac: b.hsnSac || "",
        description: b.itemName,
        quantity: b.quantity,
        unit: b.unit,
        rate: rate.toFixed(2),
        amount: (b.quantity * rate).toFixed(2),
        boqItemId: b._id,
        stage: form.stages[0] || undefined,
        directStage: null,
      };
    });

    setForm((prev) => ({ ...prev, lineItems: allItems }));
    setBoqIndex(boqItems.length);
  }, [boqItems, paymentTerms, paymentGroups, groupStages, form.stages, billingMode]);

  // ----------------------------
  // FETCH WORK ORDERS
  // ----------------------------
  const fetchWorkOrders = async () => {
    try {
      const res = await api.get("/work-orders?limit=1000");
      if (res.data.success) setWorkOrders(res.data.data);
    } catch (err) { }
  };
  const fetchBOQAndTerms = async (workOrderId) => {
    try {
      const [woRes, termRes] = await Promise.all([
        api.get(`/work-orders/${workOrderId}`),
        api.get(`/work-orders/${workOrderId}/payment-terms`),
      ]);

      setBoqItems(woRes.data?.data?.boqItems || []);

      const termsData = termRes.data?.data || {};
      const groups = termsData.paymentGroups || [];

      setPaymentMode(termsData.paymentMode || "STANDARD");
      setPaymentTerms(termsData.paymentTerms || []);
      setPaymentGroups(groups);

      // GST LOGIC (DYNAMIC STATE RESOLUTION)
      const woData = woRes.data?.data;
      setCurrentWorkOrder(woData);

      const clientState = getClientState(woData);
      const officeState = getOfficeState(woData);
      const { cgst, sgst, igst } = applyGST(clientState, officeState);

      setForm((prev) => ({ ...prev, cgst, sgst, igst }));

      // Return groups so fetchBilling can restore groupStages
      return { groups, paymentMode: termsData.paymentMode || "STANDARD", paymentTerms: termsData.paymentTerms || [], cgst, sgst, igst };
    } catch (err) {
      console.error("Failed to load BOQ / payment terms");
      return { groups: [], paymentMode: "STANDARD", paymentTerms: [] };
    }
  };

  const getClientState = (wo) => {
    return (
      wo?.project?.client?.billingAddress?.state ||
      wo?.project?.client?.shippingAddress?.state ||
      wo?.client?.billingAddress?.state ||
      wo?.project?.state ||
      wo?.project?.clientState ||
      wo?.project?.address?.state ||
      ""
    );
  };

  const getOfficeState = (wo) => {
    const office = wo?.project?.office;
    let st = "";
    if (office && typeof office === "object") {
      st = office.state || "";
      if (!st && office.address) {
        const addr = office.address.toLowerCase();
        if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
        else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
      }
    } else {
      const officeId = office?._id || office;
      const foundOffice = offices.find((o) => String(o._id) === String(officeId));
      if (foundOffice) {
        st = foundOffice.state || "";
        if (!st && foundOffice.address) {
          const addr = foundOffice.address.toLowerCase();
          if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
          else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
        }
      }
    }
    return st;
  };

  const applyGST = (clientState, officeState) => {
    const normalize = (s) => (s || "").toString().trim().toLowerCase();
    const cState = normalize(clientState);
    const oState = normalize(officeState);

    if (cState && oState && cState === oState) {
      return { cgst: 9, sgst: 9, igst: 0 };
    }
    return { cgst: 0, sgst: 0, igst: 18 };
  };

  const fetchOffices = async () => {
    try {
      const res = await api.get("/offices");
      if (res.data.success && Array.isArray(res.data.data)) {
        setOffices(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch offices", error);
    }
  };

  // ----------------------------
  // FETCH BILLING DATA
  // ----------------------------
  const fetchBilling = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/billing/${id}`);
      const billing = res.data?.data;

      if (!billing) {
        setError("Billing not found");
        return;
      }

      const workOrderId =
        typeof billing.workOrder === "object"
          ? billing.workOrder._id
          : billing.workOrder;

      // LOAD BOQ + TERMS FIRST — get groups back
      let restoredGroups = [];
      if (workOrderId) {
        const termsResult = await fetchBOQAndTerms(workOrderId);
        restoredGroups = termsResult?.groups || [];
        await fetchReportsByProject(workOrderId);
      }

      const savedStages = billing.stages || [];

      // Restore groupStages from saved billing.groupStages or saved stages + group definitions
      if (billing.groupStages && Object.keys(billing.groupStages).length > 0) {
        setGroupStages(billing.groupStages);
      } else if (restoredGroups.length && savedStages.length) {
        const restored = {};
        restoredGroups.forEach((g, gIdx) => {
          const key = `group_${gIdx}`;
          const gTerms = g.paymentTerms || [];
          const gMode = g.paymentMode || "STANDARD";
          // Find which saved stages belong to this group
          const groupStageNames = gTerms.map((t) =>
            gMode === "STANDARD" ? t.stage : t.name
          ).filter(Boolean);
          restored[key] = savedStages.filter((s) => groupStageNames.includes(s));
        });
        // Default — stages not in any group
        const allGroupStages = Object.values(restored).flat();
        const defaultStages = savedStages.filter((s) => !allGroupStages.includes(s));
        if (defaultStages.length) restored["default"] = defaultStages;
        setGroupStages(restored);
      } else {
        setGroupStages({});
      }

      const isReportBilling =
        billing.linkedReports &&
        billing.linkedReports.some(
          (g) => Array.isArray(g.reportIds) && g.reportIds.length > 0,
        );

      if (isReportBilling) {
        setBillingMode("REPORT");
        setReportItemsCache(billing.lineItems || []);
      } else {
        setBillingMode("BOQ");
      }

      setForm({
        invoiceNumber: billing.invoiceNumber || "",
        workOrder: workOrderId || "",
        stages: billing.stages || [],
        linkedReports: (billing.linkedReports || []).reduce((acc, curr) => {
          const type = curr.reportType === "MEASUREMENT_SHEET" ? "JMR" : curr.reportType;
          const existing = acc.find((r) => r.reportType === type);
          if (existing) {
            existing.reportIds = [
              ...new Set([...existing.reportIds, ...curr.reportIds]),
            ];
          } else {
            acc.push({ reportType: type, reportIds: curr.reportIds });
          }
          return acc;
        }, []),
        lineItems: billing.lineItems?.length ? billing.lineItems : [],

        subTotal: billing.subTotal || 0,

        cgst: billing.cgst || 0,
        sgst: billing.sgst || 0,
        igst: billing.igst || 0,

        cgstAmount: billing.cgstAmount || 0,
        sgstAmount: billing.sgstAmount || 0,
        igstAmount: billing.igstAmount || 0,

        totalAmount: billing.totalAmount || 0,

        status: billing.status || "",
        invoiceDocument: billing.invoiceDocument || null,
        remarks: billing.remarks || "",
        paidAmount: billing.paidAmount || 0,
      });

    } catch (err) {
      setError("Failed to load billing details");
    } finally {
      setLoading(false);
    }
  };

  const fetchReportsByProject = async (workOrderId) => {
    try {
      const [mir, jmr, tc, hand, ms, billingsRes] = await Promise.all([
        api.get(`/mir?workOrder=${workOrderId}&limit=all`),
        api.get(`/jmr?workOrder=${workOrderId}&limit=all`),
        api.get(`/testing-commissioning?workOrder=${workOrderId}&limit=all`),
        api.get(`/handover?workOrder=${workOrderId}&limit=all`),
        api.get(`/measurement-sheets?workOrder=${workOrderId}&limit=all`),
        api.get(`/billing?workOrder=${workOrderId}&limit=all`),
      ]);

      const billedReportIds = new Set();
      if (billingsRes.data.success) {
        (billingsRes.data.data || []).forEach((b) => {
          // Skip the current billing being edited
          if (String(b._id) === String(id)) return;

          (b.linkedReports || []).forEach((group) => {
            (group.reportIds || []).forEach((rid) => billedReportIds.add(String(rid)));
          });
        });
      }

      const extractApproved = (res, type) => {
        const data = res?.data?.data || [];

        const all = data.filter((r) => {
          if (type === "MIR" || type === "JMR") {
            return r.approvalStatus === "Approved";
          }

          if (type === "TC" || type === "Handover" || type === "MEASUREMENT_SHEET") {
            return r.status === "Approved";
          }

          return false;
        });

        return all.map((r) => ({
          ...r,
          isBilled: billedReportIds.has(String(r._id)),
        }));
      };

      setReports({
        MIR: extractApproved(mir, "MIR").map((r) => ({ ...r, actualType: "MIR" })),
        JMR: [
          ...extractApproved(jmr, "JMR").map((r) => ({ ...r, actualType: "JMR" })),
          ...extractApproved(ms, "MEASUREMENT_SHEET").map((r) => ({
            ...r,
            actualType: "MEASUREMENT_SHEET",
          })),
        ],
        TC: extractApproved(tc, "TC").map((r) => ({ ...r, actualType: "TC" })),
        Handover: extractApproved(hand, "Handover").map((r) => ({
          ...r,
          actualType: "Handover",
        })),
      });
    } catch (err) {
      console.error("Failed to load reports", err);
    }
  };

  const recalculateReportLineItems = (items = []) => {
    const anyStageSelected = paymentGroups.length
      ? Object.values(groupStages).some((s) => s && s.length > 0)
      : form.stages.length > 0;

    if (!boqItems.length || (!paymentTerms.length && !paymentGroups.length) || !anyStageSelected) return;

    const reportToStageMap = {
      MIR: "Supply",
      JMR: "Installation",
      MEASUREMENT_SHEET: "Installation",
      TC: "Testing & Commissioning",
      Handover: "Handover",
    };

    const cleanName = (str) =>
      str?.replace(/\[WLI-.*?\]\s*/i, "").replace(/\s+/g, " ").trim().toLowerCase();

    // 1. Get active BOQ items based on paymentGroups or global stages
    const activeBoqItems = paymentGroups.length
      ? boqItems.filter((b) => {
          const config = getPaymentConfigForBoq(b);
          const key = config.groupKey || "default";
          const selected = groupStages[key] || [];
          return selected.length > 0;
        })
      : boqItems;

    const consumedReportIndices = new Set();

    // 2. Map every active BOQ item, merging with reportItem if available
    const mergedItems = activeBoqItems.map((b) => {
      let stagePercent = 0;
      const config = getPaymentConfigForBoq(b);
      const effectiveStages = getEffectiveStagesForBoq(b);
      if (config.mode === "CUSTOM") {
        stagePercent = getTotalPercentForStages(effectiveStages, b);
      } else {
        const stdStage = effectiveStages.length > 0 ? effectiveStages[0] : "";
        const term = getTermForStage(stdStage, b);
        stagePercent = Number(term?.percentage || 0);
        if (stagePercent === 0) {
          stagePercent = getTotalPercentForStages(effectiveStages, b);
        }
      }

      const rate = (Number(b.price || 0) * stagePercent) / 100;
      const stage = effectiveStages.length > 0 ? effectiveStages.join(", ") : "";

      // Search matching item in report items
      let matchedRepItem = null;
      let matchedIdx = -1;

      for (let i = 0; i < (items || []).length; i++) {
        if (consumedReportIndices.has(i)) continue;
        const rep = items[i];

        // Match by BOQ ID
        if (rep.boqItemId && String(rep.boqItemId) === String(b._id)) {
          matchedRepItem = rep;
          matchedIdx = i;
          break;
        }

        // Match by wlino
        const repWlino = rep.wlino || rep.source?.wlino;
        if (repWlino && b.wlino && String(repWlino).trim() === String(b.wlino).trim()) {
          matchedRepItem = rep;
          matchedIdx = i;
          break;
        }

        // Match by [WLI-...] regex
        const text = rep.description || rep.itemName || "";
        const m = text.match(/\[WLI-(.*?)\]/i);
        if (m && m[1] && b.wlino && String(m[1]).trim() === String(b.wlino).trim()) {
          matchedRepItem = rep;
          matchedIdx = i;
          break;
        }

        // Match by clean name
        const repName = cleanName(rep.itemName || rep.description);
        const boqName = cleanName(b.itemName);
        if (repName && boqName && (repName === boqName || repName.includes(boqName) || boqName.includes(repName))) {
          matchedRepItem = rep;
          matchedIdx = i;
          break;
        }
      }

      if (matchedRepItem) {
        consumedReportIndices.add(matchedIdx);
        const existing = (form.lineItems || []).find((it) => (it.boqItemId && String(it.boqItemId) === String(b._id)) || (it.wlino && b.wlino && String(it.wlino).trim() === String(b.wlino).trim()));
        const repQty = (existing && existing.quantity !== undefined && existing.quantity !== null && existing.quantity !== "") ? Number(existing.quantity) : Number(matchedRepItem.quantity || 0);
        const repType = matchedRepItem.source?.reportType;
        const repStage = stage || (paymentMode === "STANDARD" ? reportToStageMap[repType] : "");

        return {
          wlino: b.wlino || matchedRepItem.wlino || "",
          hsnSac: b.hsnSac || matchedRepItem.hsnSac || "",
          description: b.itemName || matchedRepItem.itemName || matchedRepItem.description,
          quantity: repQty,
          unit: b.unit || matchedRepItem.unit || "Nos",
          rate: rate.toFixed(2),
          amount: (repQty * rate).toFixed(2),
          boqItemId: b._id,
          stage: stage || repStage,
          stagePercentage: stagePercent,
          billingType: "REPORT",
          source: matchedRepItem.source,
        };
      }

      // If not matched in report, keep BOQ item intact in the table!
      const existing = (form.lineItems || []).find((it) => (it.boqItemId && String(it.boqItemId) === String(b._id)) || (it.wlino && b.wlino && String(it.wlino).trim() === String(b.wlino).trim()));
      const boqQty = (existing && existing.quantity !== undefined && existing.quantity !== null && existing.quantity !== "") ? Number(existing.quantity) : Number(b.quantity || 0);
      return {
        wlino: b.wlino || "",
        hsnSac: b.hsnSac || "",
        description: b.itemName,
        quantity: boqQty,
        unit: b.unit || "Nos",
        rate: rate.toFixed(2),
        amount: (boqQty * rate).toFixed(2),
        boqItemId: b._id,
        stage,
        stagePercentage: stagePercent,
        billingType: "BOQ",
        source: null,
      };
    });

    // 3. Append any unmatched report items (extra manual items in report)
    (items || []).forEach((rep, idx) => {
      if (consumedReportIndices.has(idx)) return;
      const repType = rep.source?.reportType;
      const stdStage = reportToStageMap[repType] || "";
      const selectedCustomStage = form.stages?.[0] || Object.values(groupStages).flat()[0] || "";
      const repStage = selectedCustomStage || (paymentMode === "STANDARD" ? stdStage : "");
      const repQty = Number(rep.quantity || 0);
      const repRate = Number(rep.rate || 0);

      mergedItems.push({
        wlino: rep.wlino || "",
        hsnSac: rep.hsnSac || "",
        description: rep.itemName || rep.description || "",
        quantity: repQty,
        unit: rep.unit || "Nos",
        rate: repRate.toFixed(2),
        amount: (repQty * repRate).toFixed(2),
        boqItemId: rep.boqItemId || null,
        stage: repStage || stdStage || "",
        stagePercentage: 0,
        billingType: "REPORT",
        source: rep.source,
      });
    });

    setForm((prev) => ({
      ...prev,
      lineItems: mergedItems,
    }));
  };

  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    const anyStageSelected = paymentGroups.length
      ? Object.values(groupStages).some((s) => s && s.length > 0)
      : form.stages.length > 0;
    if (!anyStageSelected) return;

    recalculateReportLineItems(reportItemsCache);
  }, [form.stages, groupStages, paymentTerms, paymentGroups, boqItems, paymentMode]);

  const fetchBillingSourceItems = async (reportType, reportId) => {
    try {
      const res = await api.get(`/billing-source/${reportType}/${reportId}`);
      const items = res.data?.data?.billingItems || [];

      setBillingMode("REPORT");
      setReportItemsCache(items);

      recalculateReportLineItems(items);
    } catch (err) {
      swal("Error", "Failed to load billing items", "error");
    }
  };

  // ----------------------------
  // HANDLE FORM CHANGES
  // ----------------------------
  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === "workOrder") {
      setForm((prev) => ({
        ...prev,
        workOrder: value,
        stages: [],
        linkedReports: [],
        lineItems: [],
        cgst: 0,
        sgst: 0,
        igst: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
      }));

      setReports({ MIR: [], JMR: [], TC: [], Handover: [], MEASUREMENT_SHEET: [] });

      if (value) {
        await fetchBOQAndTerms(value);
        await fetchReportsByProject(value);
      }

      return;
    }

    setForm((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "status") {
        if (value === "Paid") {
          updated.paidAmount = prev.totalAmount || 0;
        }

        if (value !== "Paid" && value !== "Partially Paid") {
          updated.paidAmount = 0;
        }
      }

      return updated;
    });
  };
  const openStageModal = (index) => {
    setActiveItemIndex(index);

    setDirectStages({
      installation: false,
      tc: false,
      handover: false,
    });

    setShowStageModal(true);
  };
  const applyDirectStages = () => {
    if (activeItemIndex === null) return;

    if (
      !directStages.installation &&
      !directStages.tc &&
      !directStages.handover
    ) {
      swal("Select Stage", "Please choose a direct stage", "warning");
      return;
    }

    const item = form.lineItems[activeItemIndex];

    const boq = boqItems.find((b) => String(b._id) === String(item.boqItemId));

    if (!boq) return;

    const stageOrder = [
      "Supply",
      "Installation",
      "Testing & Commissioning",
      "Handover",
    ];

    let targetStage = null;

    if (directStages.handover) targetStage = "Handover";
    else if (directStages.tc) targetStage = "Testing & Commissioning";
    else if (directStages.installation) targetStage = "Installation";

    // Stage must be selected in billing stages
    if (!form.stages.includes(targetStage)) {
      swal(
        "Invalid Stage",
        `${targetStage} is not selected in Billing Stages`,
        "error",
      );
      return;
    }

    const stageIndex = stageOrder.indexOf(targetStage);

    let totalPercent = 0;

    for (let i = 0; i <= stageIndex; i++) {
      const stageName = stageOrder[i];

      if (!form.stages.includes(stageName)) continue;

      const term = getTermForStage(stageName, boq);

      if (term) totalPercent += Number(term.percentage || 0);
    }

    const rate = (Number(boq.price) * totalPercent) / 100;
    const qty = Number(item.quantity || 0);

    const updated = [...form.lineItems];

    updated[activeItemIndex] = {
      ...item,
      rate: rate.toFixed(2),
      amount: (qty * rate).toFixed(2),
      directStage: targetStage,
    };

    setForm((prev) => ({
      ...prev,
      lineItems: updated,
    }));

    setShowStageModal(false);
  };

  // const handleLinkedReportChange = async (e) => {
  // const { name, value } = e.target;

  // setForm((prev) => ({
  // ...prev,
  // linkedReport: {
  // ...prev.linkedReport,
  // [name]: value,
  // ...(name === "reportType" ? { reportId: "" } : {}),
  // },
  // }));
  // if (name === "reportType") {
  // setBillingMode("REPORT");
  // }

  // // Only refetch when user selects a NEW report
  // if (name === "reportId") {
  // const confirm = await swal({
  // title: "Replace Line Items?",
  // text: "This will replace current invoice items with report data",
  // icon: "warning",
  // buttons: true,
  // dangerMode: true,
  // });

  // if (confirm) {
  // await fetchBillingSourceItems(form.linkedReport.reportType, value);
  // }
  // }
  // };

  // ----------------------------
  // LINE ITEM CHANGE + AMOUNT CALC
  // ----------------------------
  const handleLineItemChange = (index, field, value) => {
    const items = [...form.lineItems];
    items[index][field] = value;

    if (field === "quantity" || field === "rate") {
      items[index].amount = (
        parseFloat(items[index].quantity || 0) *
        parseFloat(items[index].rate || 0)
      ).toFixed(2);
    }

    setForm((prev) => ({ ...prev, lineItems: items }));
  };

  // ----------------------------
  // ADD & REMOVE LINE ITEMS
  // ----------------------------
  const addLineItem = () => {
    setForm((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          wlino: "",
          hsnSac: "",
          description: "",
          quantity: 0,
          unit: "pcs",
          rate: 0,
          amount: 0,
        },
      ],
    }));
  };

  const removeLineItem = (index) => {
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  // ----------------------------
  // FILE UPLOAD
  // ----------------------------
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setForm((prev) => ({
        ...prev,
        invoiceDocument: {
          fileName: file.name,
          fileUrl: reader.result,
        },
      }));
    };
    reader.readAsDataURL(file);
  };

  // ----------------------------
  // SUBMIT UPDATE
  // ----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.invoiceNumber || !form.workOrder) {
      swal(
        "Missing Information",
        "Invoice number and work order are required",
        "warning",
      );
      return;
    }

    if (!form.lineItems || form.lineItems.length === 0) {
      swal(
        "Missing Line Items",
        "At least one line item is required to save the invoice.",
        "warning",
      );
      return;
    }

    swal({
      title: "Updating Billing",
      text: "Please wait...",
      icon: "info",
      buttons: false,
      closeOnClickOutside: false,
    });

    try {
      setSubmitting(true);

      const payload = {
        ...form,
        groupStages: paymentGroups.length ? groupStages : undefined,
        // In group mode, flatten all selected stages across groups
        stages: paymentGroups.length
          ? [...new Set(Object.values(groupStages).flat())]
          : form.stages,
        linkedReports: (form.linkedReports || []).flatMap((g) => {
          const typeMap = {};
          (g.reportIds || []).forEach((id) => {
            const report = reports[g.reportType]?.find((r) => r._id === id);
            const actualType = report?.actualType || g.reportType;
            if (!typeMap[actualType]) typeMap[actualType] = [];
            typeMap[actualType].push(id);
          });

          return Object.keys(typeMap).map((type) => ({
            reportType: type,
            reportIds: typeMap[type],
          }));
        }),
        lineItems: (form.lineItems || []).map((item) => ({
          wlino: item.wlino || "",
          hsnSac: item.hsnSac || "",
          description: item.description,
          quantity: Number(item.quantity || 0),
          unit: item.unit,
          rate: Number(item.rate || 0),
          amount: Number(item.amount || 0),
          boqItemId: item.boqItemId || undefined,
          stage: item.stage || undefined,
          stagePercentage: Number(item.stagePercentage || 0),
          directStage: item.directStage || undefined,
          source: item.source
            ? {
              reportType: item.source.reportType,
              reportId: item.source.reportId,
              itemId: item.source.itemId,
            }
            : undefined,
        })),
        subTotal: Number(form.subTotal || 0),
        cgst: Number(form.cgst || 0),
        sgst: Number(form.sgst || 0),
        igst: Number(form.igst || 0),
        cgstAmount: Number(form.cgstAmount || 0),
        sgstAmount: Number(form.sgstAmount || 0),
        igstAmount: Number(form.igstAmount || 0),
        totalAmount: Number(form.totalAmount || 0),
      };

      const res = await api.patch(`/billing/${id}`, payload);

      swal.close();

      if (res.data.success) {
        if (res.data.data?.groupStages) { setGroupStages(res.data.data.groupStages); }
        if (res.data.data?.lineItems) {
          setForm((prev) => ({ ...prev, lineItems: res.data.data.lineItems }));
        }
        swal({
          title: "Billing Updated",
          text: "Invoice updated successfully",
          icon: "success",
          buttons: {
            cancel: "Stay",
            confirm: "Go to Billing List",
          },
        }).then((go) => {
          if (go) {
            navigate(
              workOrderFromUrl
                ? `/billings?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                : "/billings"
            );
          }
        });
      }
    } catch (err) {
      swal.close();
      swal("Update Failed", "Failed to update billing", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ----------------------------
  // AUTO RECALCULATE
  // ----------------------------
  useEffect(() => {
    if (loading) return;

    const subTotal = form.lineItems.reduce(
      (sum, item) =>
        sum + parseFloat(item.quantity || 0) * parseFloat(item.rate || 0),
      0,
    );

    const cgstAmount = (subTotal * form.cgst) / 100;
    const sgstAmount = (subTotal * form.sgst) / 100;
    const igstAmount = (subTotal * form.igst) / 100;

    const totalAmount = subTotal + cgstAmount + sgstAmount + igstAmount;

    setForm((prev) => ({
      ...prev,
      subTotal: subTotal.toFixed(2),
      cgstAmount: cgstAmount.toFixed(2),
      sgstAmount: sgstAmount.toFixed(2),
      igstAmount: igstAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    }));
  }, [form.lineItems, form.cgst, form.sgst, form.igst, loading]);

  if (loading) {
    return (
      <Fragment>
        <PageTitle activeMenu="Edit Billing" motherMenu="Billing Management" />
        <Row>
          <Col lg={12}>
            <Card>
              <Card.Body className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading billing details...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageTitle activeMenu="Edit Billing" motherMenu="Billing Management" />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title>Edit Billing / Invoice</Card.Title>

              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    workOrderFromUrl
                      ? `/billings?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                      : "/billings"
                  )
                }
              >
                <i className="fas fa-arrow-left me-2"></i>Back
              </Button>
            </Card.Header>

            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* =============================== */}
                {/* BASIC INFORMATION */}
                {/* =============================== */}
                <div className="border-bottom pb-3 mb-4">
                  <h5>
                    <i className="fas fa-info-circle me-2"></i>Basic Information
                  </h5>

                  <Row>
                    {/* Invoice Number */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Invoice Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="invoiceNumber"
                          value={form.invoiceNumber}
                          readOnly
                        />
                      </Form.Group>
                    </Col>

                    {/* Status */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          name="status"
                          value={form.status}
                          onChange={handleChange}
                        >
                          {statuses.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    {/* Project */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Work Order</Form.Label>

                        <Form.Select
                          name="workOrder"
                          value={form.workOrder}
                          onChange={handleChange}
                        >
                          <option value="">Select Work Order</option>
                          {filteredWorkOrders.map((wo) => (
                            <option key={wo._id} value={wo._id}>
                              {wo.woNo}{wo.label ? ` – ${wo.label}` : ""} — {wo.project?.projectName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {/* Stage */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Stage</Form.Label>

                        {paymentGroups.length > 0 ? (
                          <div className="border rounded p-2" style={{ background: "#f8f9fa" }}>
                            {paymentGroups.map((g, gIdx) => {
                              const key = `group_${gIdx}`;
                              const opts = getStagesForGroup(gIdx);
                              return (
                                <div key={gIdx} className="d-flex align-items-center gap-2 mb-2">
                                  <span className="badge bg-primary" style={{ minWidth: 80, fontSize: 11 }}>
                                    {g.groupName}
                                  </span>
                                  <div style={{ flex: 1 }}>
                                    <Select
                                      isMulti
                                      options={opts.map((s) => ({ value: s, label: s }))}
                                      value={(groupStages[key] || []).map((s) => ({ value: s, label: s }))}
                                      onChange={(sel) => {
                                        const selected = sel ? sel.map((o) => o.value) : [];
                                        const updatedGrpStages = { ...groupStages, [key]: selected };
                                        setGroupStages(updatedGrpStages);
                                        const flattenedStages = [...new Set(Object.values(updatedGrpStages).flat())];
                                        setForm((prev) => {
                                          const updatedLineItems = recalculateBoqLineItems(prev.lineItems, flattenedStages, updatedGrpStages);
                                          return { ...prev, stages: flattenedStages, lineItems: updatedLineItems };
                                        });
                                        if (form.workOrder) fetchReportsByProject(form.workOrder);
                                      }}
                                      placeholder="Select stages..."
                                      classNamePrefix="react-select"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {boqItems.some((b) => {
                              const assignedIds = new Set(paymentGroups.flatMap((g) => (g.boqItemIds || []).map(String)));
                              return !assignedIds.has(String(b._id));
                            }) && (
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge bg-secondary" style={{ minWidth: 80, fontSize: 11 }}>Default</span>
                                  <div style={{ flex: 1 }}>
                                    <Select
                                      isMulti
                                      options={getStagesForDefault().map((s) => ({ value: s, label: s }))}
                                      value={(groupStages["default"] || []).map((s) => ({ value: s, label: s }))}
                                      onChange={(sel) => {
                                        const selected = sel ? sel.map((o) => o.value) : [];
                                        const updatedGrpStages = { ...groupStages, default: selected };
                                        setGroupStages(updatedGrpStages);
                                        const flattenedStages = [...new Set(Object.values(updatedGrpStages).flat())];
                                        setForm((prev) => {
                                          const updatedLineItems = recalculateBoqLineItems(prev.lineItems, flattenedStages, updatedGrpStages);
                                          return { ...prev, stages: flattenedStages, lineItems: updatedLineItems };
                                        });
                                      }}
                                      placeholder="Select stages..."
                                      classNamePrefix="react-select"
                                    />
                                  </div>
                                </div>
                              )}
                          </div>
                        ) : (
                          <Select
                            isMulti
                            options={stages.map((st) => ({ value: st, label: st }))}
                            value={form.stages?.map((st) => ({ value: st, label: st })) || []}
                            onChange={(selectedOptions) => {
                              const selected = selectedOptions ? selectedOptions.map(o => o.value) : [];
                              setForm((prev) => {
                                const updatedLineItems = recalculateBoqLineItems(prev.lineItems, selected);
                                return { ...prev, stages: selected, lineItems: updatedLineItems };
                              });
                              if (form.workOrder) fetchReportsByProject(form.workOrder);
                            }}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* =============================== */}
                {/* LINKED REPORT */}
                {/* =============================== */}
                <div className="border-bottom pb-3 mb-4">
                  <h5>
                    <i className="fas fa-link me-2"></i>Linked Report (Optional)
                  </h5>

                  <Row>
                    {/* Report Type */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Report Type</Form.Label>
                        <Select
                          isMulti
                          options={visibleReportTypes.map((type) => ({ value: type, label: type }))}
                          value={form.linkedReports.map((r) => ({ value: r.reportType, label: r.reportType }))}
                          onChange={(selectedOptions) => {
                            const selectedTypes = selectedOptions ? selectedOptions.map(o => o.value) : [];

                            const updated = selectedTypes.map((type) => ({
                              reportType: type,
                              reportIds: [],
                            }));

                            setForm((prev) => ({
                              ...prev,
                              linkedReports: updated,
                            }));

                            if (updated.length === 0) {
                              setReportItemsCache([]);
                              recalculateReportLineItems([]);
                            }

                            fetchReportsByProject(form.workOrder);
                          }}
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                      </Form.Group>
                    </Col>

                    {/* Report List */}
                    <Col md={6}>
                      {form.linkedReports.map((group, index) => (
                        <Form.Group key={group.reportType} className="mb-3">
                          <Form.Label>{group.reportType} Reports</Form.Label>

                          <Select
                            isMulti
                            options={reports[group.reportType]?.map((r) => ({
                              value: r._id,
                              label: `${r.mirNumber || r.jmrNumber || r.tcNumber || r.hoNumber || r.sheetNumber || r._id} ${r.isBilled ? "(Billed)" : ""}`
                            })) || []}
                            value={(group.reportIds || []).map(id => {
                              const r = reports[group.reportType]?.find(rep => rep._id === id);
                              return {
                                value: id,
                                label: r ? `${r.mirNumber || r.jmrNumber || r.tcNumber || r.hoNumber || r.sheetNumber || r._id} ${r.isBilled ? "(Billed)" : ""}` : id
                              };
                            })}
                            onChange={async (selectedOptions) => {
                              const selectedIds = selectedOptions ? selectedOptions.map(o => o.value) : [];

                              const updated = [...form.linkedReports];
                              updated[index].reportIds = selectedIds;

                              setForm((prev) => ({
                                ...prev,
                                linkedReports: updated,
                              }));

                              try {
                                // Collect ALL items from ALL selected reports
                                let allItems = [];

                                for (const g of updated) {
                                  for (const id of g.reportIds) {
                                    const report = reports[g.reportType]?.find(r => r._id === id);
                                    const actualType = report?.actualType || g.reportType;

                                    const res = await api.get(
                                      `/billing-source/${actualType}/${id}`,
                                    );

                                    const items =
                                      res.data?.data?.billingItems || [];
                                    allItems = [...allItems, ...items];
                                  }
                                }

                                // If nothing selected → clear safely
                                if (!allItems.length) {
                                  setReportItemsCache([]);
                                  recalculateReportLineItems([]);
                                  return;
                                }

                                setReportItemsCache(allItems);
                                recalculateReportLineItems(allItems);
                              } catch (error) {
                                swal(
                                  "Error",
                                  error.response?.data?.message ||
                                  "Failed to fetch report items",
                                  "error",
                                );
                              }
                            }}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </Form.Group>
                      ))}
                    </Col>
                  </Row>
                </div>

                {/* =============================== */}
                {/* LINE ITEMS */}
                {/* =============================== */}
                <div className="border-bottom pb-3 mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <i className="fas fa-list me-2"></i>Line Items
                    </h5>

                    {billingMode !== "BOQ" && (
                      <Button size="sm" onClick={addLineItem}>
                        + Add Item
                      </Button>
                    )}
                  </div>
                  {billingMode === "BOQ" &&
                    form.lineItems.length < boqItems.length && (
                      <div className="text-center mt-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={loadNextBoqItems}
                        >
                          + Load Next BOQ Items
                        </Button>

                        <div className="text-muted mt-1">
                          Showing {form.lineItems.length} of {boqItems.length}{" "}
                          BOQ items
                        </div>
                      </div>
                    )}

                  <div className="table-responsive">
                    <Table bordered hover>
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>WLINo</th>
                          <th>HSN/SAC</th>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Rate</th>
                          <th>Amount</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {groupedItems.map((group, gIndex) => (
                          <Fragment key={gIndex}>
                            {/* GROUP HEADER */}
                            <tr className="table-primary billing-report-group-row">
                              <td colSpan="9">
                                <strong className="billing-report-group-title">
                                  {group.reportType === "BOQ"
                                    ? "BOQ Items"
                                    : group.reportNumber
                                      ? `${group.reportType === "MEASUREMENT_SHEET" ? "JMR" : group.reportType} — ${group.reportNumber}`
                                      : group.reportType === "MEASUREMENT_SHEET" ? "JMR" : group.reportType}
                                </strong>

                                <span className="ms-2 billing-report-group-count">
                                  ({group.items.length} items)
                                </span>
                              </td>
                            </tr>

                            {/* ITEMS INSIDE GROUP */}
                            {group.items.map((item, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>
                                  <Form.Control
                                    value={item.wlino || ""}
                                    readOnly
                                    size="sm"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    value={item.hsnSac || ""}
                                    readOnly
                                    size="sm"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    value={item.description}
                                    onChange={(e) =>
                                      handleLineItemChange(
                                        item.originalIndex,
                                        "description",
                                        e.target.value,
                                      )
                                    }
                                    size="sm"
                                  />
                                </td>

                                <td>
                                  <Form.Control
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) =>
                                      handleLineItemChange(
                                        item.originalIndex,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                    size="sm"
                                  />
                                </td>

                                <td>
                                  <Form.Control value={item.unit} disabled />
                                </td>

                                <td>
                                  <Form.Control
                                    type="number"
                                    value={item.rate}
                                    readOnly
                                  />
                                </td>

                                <td>
                                  <Form.Control
                                    value={item.amount}
                                    readOnly
                                    size="sm"
                                  />
                                </td>

                                <td className="text-center d-flex gap-1 justify-content-center">
                                  {/* Stage Button — only for BOQ items */}
                                  {paymentMode === "STANDARD" && (
                                    <Button
                                      variant="warning"
                                      size="sm"
                                      onClick={() =>
                                        openStageModal(item.originalIndex)
                                      }
                                    >
                                      Stage
                                    </Button>
                                  )}

                                  {/* Delete */}
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() =>
                                      removeLineItem(item.originalIndex)
                                    }
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>

                {/* =============================== */}
                {/* FINANCIAL SUMMARY */}
                {/* =============================== */}
                <div className="border-bottom pb-3 mb-4">
                  <h5>
                    <i className="fas fa-calculator me-2"></i>Financial Summary
                  </h5>

                  <Row>
                    <Col md={8}></Col>

                    <Col md={4}>
                      <div className="d-flex justify-content-between mb-2">
                        <strong>Sub Total:</strong>
                        <span>₹ {form.subTotal}</span>
                      </div>

                      {form.cgst > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <strong>CGST ({form.cgst}%):</strong>
                          <span>₹ {form.cgstAmount}</span>
                        </div>
                      )}

                      {form.sgst > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <strong>SGST ({form.sgst}%):</strong>
                          <span>₹ {form.sgstAmount}</span>
                        </div>
                      )}

                      {form.igst > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <strong>IGST ({form.igst}%):</strong>
                          <span>₹ {form.igstAmount}</span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between border-top pt-2">
                        <strong>Total:</strong>
                        <strong className="text-primary">
                          ₹ {form.totalAmount}
                        </strong>
                      </div>

                      {(form.status === "Paid" ||
                        form.status === "Partially Paid") && (
                          <div className="mt-3">
                            <Form.Label>Paid Amount</Form.Label>
                            <Form.Control
                              type="number"
                              name="paidAmount"
                              value={form.paidAmount}
                              onChange={handleChange}
                            />
                            <small className="text-muted">
                              Pending: ₹{" "}
                              {(
                                parseFloat(form.totalAmount || 0) -
                                parseFloat(form.paidAmount || 0)
                              ).toFixed(2)}
                            </small>
                          </div>
                        )}
                    </Col>
                  </Row>
                </div>


                {/* =============================== */}
                {/* DOCUMENTS */}
                {/* =============================== */}
                <div className="pb-3 mb-4">
                  <h5>
                    <i className="fas fa-file-upload me-2"></i>Documents &
                    Remarks
                  </h5>

                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Upload Invoice</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={handleFile}
                        />
                        <FilePreview
                          file={form.invoiceDocument}
                          onRemove={() => setForm(prev => ({ ...prev, invoiceDocument: null }))}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="remarks"
                          value={form.remarks}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* =============================== */}
                {/* SUBMIT */}
                {/* =============================== */}
                <div className="d-flex gap-2">
                  <Button type="submit" variant="primary" disabled={submitting}>
                    {submitting ? "Updating..." : "Update Billing"}
                  </Button>

                  <Button
                    variant="danger"
                    type="button"
                    onClick={() =>
                      navigate(
                        workOrderFromUrl
                          ? `/billings?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                          : "/billings"
                      )
                    }
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal
        show={showStageModal}
        onHide={() => setShowStageModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Direct Stage Billing</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Check
              type="checkbox"
              label="Direct Installation"
              checked={directStages.installation}
              onChange={(e) =>
                setDirectStages({
                  installation: e.target.checked,
                  tc: false,
                  handover: false,
                })
              }
            />

            <Form.Check
              type="checkbox"
              label="Direct Testing & Commissioning"
              checked={directStages.tc}
              onChange={(e) =>
                setDirectStages({
                  installation: false,
                  tc: e.target.checked,
                  handover: false,
                })
              }
            />

            <Form.Check
              type="checkbox"
              label="Direct Handover"
              checked={directStages.handover}
              onChange={(e) =>
                setDirectStages({
                  installation: false,
                  tc: false,
                  handover: e.target.checked,
                })
              }
            />
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStageModal(false)}>
            Cancel
          </Button>

          <Button variant="primary" onClick={applyDirectStages}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default BillingEdit;
